import { useState, useEffect, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { createCompound, getCompounds, updateCompound, deleteCompound } from '../../../../../api/CompoundsApi';

const escapeRegex = str => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const useCompounds = () => {
    const [compounds, setCompounds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const fetchCompounds = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getCompounds();
            setCompounds(data?.compounds || []);
        } catch (e) {
            console.error(e);
            message.error('Failed to load compounds');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCompounds();
    }, [fetchCompounds]);

    const filteredCompounds = useMemo(() => {
        const sorted = [...compounds].sort((a, b) =>
            (a.name ?? '').localeCompare(b.name ?? '')
        );
        if (!search.trim()) return sorted;
        const regex = new RegExp(escapeRegex(search.trim()), 'i');
        return sorted.filter(c => regex.test(c.name));
    }, [compounds, search]);

    const handleCreate = useCallback(async (values) => {
        try {
            const { data } = await createCompound(values);
            const created = data?.compound;
            if (created) {
                setCompounds(prev => [...prev, created]);
            }
            message.success('Compound created');
            return true;
        } catch (e) {
            console.error(e);
            message.error('Failed to create compound');
            return false;
        }
    }, []);

    const handleUpdate = useCallback(async (compoundId, values) => {
        try {
            const { data } = await updateCompound(compoundId, values);
            const updated = data?.compound;
            if (updated) {
                setCompounds(prev => prev.map(c => c._id === compoundId ? updated : c));
            }
            message.success('Compound updated');
            return true;
        } catch (e) {
            console.error(e);
            message.error('Failed to update compound');
            return false;
        }
    }, []);

    const handleDelete = useCallback(async (compoundId) => {
        try {
            await deleteCompound(compoundId);
            setCompounds(prev => prev.filter(c => c._id !== compoundId));
            message.success('Compound deleted');
        } catch (e) {
            console.error(e);
            message.error('Failed to delete compound');
        }
    }, []);

    return {
        compounds: filteredCompounds,
        loading,
        search,
        setSearch,
        handleCreate,
        handleUpdate,
        handleDelete,
    };
};
